import React, { useEffect, useMemo, useRef, useState } from 'react'
import { gsap } from 'gsap'
import Scrollbar from 'react-scrollbars-custom'
import { CSSTransition, SwitchTransition } from 'react-transition-group'
import classNames from 'classnames'
import Toggle from '../../baseComponents/gui/toggle/Toggle'
import AppLoader from '../../features/appLoader/AppLoader'
import Room from '../../features/room/Room'
import CustomizationFilters from './customizationFilters/CustomizationFilters'
import CustomizationBlock from './customizationBlock/CustomizationBlock'
import StatusPanel from '../../baseComponents/gui/statusPanel/StatusPanel'
import { useRequests } from '../../../redux/reducer/requests'
import { useElf } from '../../../redux/reducer/elf'
import { useContent, useProducts } from '../../../redux/reducer/content'
import CustomizationRooms from './customizationRooms/CustomizationRooms'
import { defaultStore, stores, useTranslation } from '../../../translations'
import { exeptionRoom } from '../../../constants/exeptionRoom'
import {
  customizationContent,
  customizationScrollbarSettings,
  august2026ChangesData,
} from '../../../constants/copyright'
import { isLocalOrTestHost } from '../../../utils/is-localhost'
import { useBuyOrPutItem } from '../../../hooks/useBuyOrPutItem'
import { useCustomizationInfoModals } from '../../../hooks/useCustomizationInfoModals'
import { useFeatureToggle } from '../../../hooks/useFeatureToggle'
import { useClothType } from '../../../hooks/useClothType'
import { useUser } from '../../../redux/reducer/user'
import { useGetProductsAndClear } from '../../features/product'
import CustomizationBanner from './customizationBanner/CustomizationBanner'
import { useChangesAugust2026 } from '../../../hooks/useChangesAugust2026'

// мапа какой шаг показывать для каждого таба
const stepsLogic = { character: 'filters', room: 'rooms', beauties: 'filters' }

const Customization = React.forwardRef(function Customization({ callbacks }, ref) {
  const scrollRef = useRef()
  const { shopItems, eventRooms, notFilteredShopItems } = useContent()
  const {
    authData: { isWeb },
  } = useRequests()
  const { activeRoom, isDressed } = useElf()
  const { t } = useTranslation()
  const [getWebCatalogProductsBySku] = useGetProductsAndClear()

  const gameTamagotchiBeauties = useFeatureToggle('gameTamagotchiBeauties')

  // По id товара возвращает общий тип товара (character/room/shower)
  const getSlotType = useClothType()
  const { buyProducts } = useProducts()
  const {
    profile: { items },
  } = useUser()

  const [customState, setCustomState] = useState('character')
  const [customStep, setCustomStep] = useState('rooms')
  const [filter, setFilter] = useState(null)

  // handler для переключения табов в Toggle
  const shopState = {
    character: () => setCustomState('character'),
    room: () => setCustomState('room'),
    beauties: () => setCustomState('beauties'),
  }

  // Флаг отображения фильтров и списка товаров
  const isVisibleCustomizationSlots = useMemo(
    () =>
      customState === 'beauties'
        ? !!notFilteredShopItems // Для beauties нужны товары без фильтрации
        : !!shopItems && !!shopItems[customState],
    [shopItems, customState, notFilteredShopItems],
  )

  // Массив SKU продуктов за бьютисы/гифты
  const webCatalogProductsSku = useRef([])

  // Массив фильтров-слотов для CustomizationFilters
  const filteredSlots = useMemo(() => {
    if (!shopItems) {
      return []
    }

    const shopItemsArray =
      customState === 'beauties' ? notFilteredShopItems : Object.values(shopItems[customState])

    return {
      character() {
        return shopItemsArray
      },
      room() {
        const truthRoom =
          typeof exeptionRoom !== 'string'
            ? activeRoom
            : (() => {
                const necessaryId = eventRooms?.find(
                  ({ system_name }) => system_name === exeptionRoom,
                )?.id

                return ['object', 'number'].includes(typeof necessaryId)
                  ? necessaryId
                  : eventRooms?.find(({ system_name }) => system_name === 'origin')?.id
              })()

        return shopItemsArray.reduce((acc, slot) => {
          if (slot?.eventRoomId !== truthRoom) {
            return acc
          }
          acc[slot.system_name.endsWith('_room') ? 'unshift' : 'push'](slot)

          return acc
        }, [])
      },
      beauties() {
        const { beautiesSection } = customizationContent

        webCatalogProductsSku.current = []

        const necessaryItems = shopItemsArray.reduce((acc, itemData) => {
          const { id, product_id } = itemData

          // Проходят товары только с product_id
          if (typeof product_id !== 'number') {
            return acc
          }

          const product = buyProducts?.find(({ id }) => id === product_id)

          const profileItem = items?.find(({ id: itemId }) => itemId === id)

          const status = !profileItem
            ? 'absent'
            : profileItem?.is_equipped
            ? 'equipped'
            : 'unequipped'

          if (product?.sku) {
            webCatalogProductsSku.current.push(product.sku)
          }

          return [...acc, { ...itemData, slot_type: getSlotType(id), product, status }]
        }, [])

        if (!necessaryItems.length) {
          return []
        }

        return Object.keys(beautiesSection(t)).reduce((acc, key) => {
          acc.push({
            ...beautiesSection(t)[key],
            array: necessaryItems.filter(
              (item) => item.slot_type === beautiesSection(t)[key].system_name,
            ),
          })

          return acc
        }, [])
      },
    }[customState]?.()
  }, [shopItems, eventRooms, customState, activeRoom, notFilteredShopItems, buyProducts, items])

  // Массив товаров для CustomizationBlock (при клике на фильтр)
  const filteredList = useMemo(() => {
    if (!shopItems) {
      return []
    }

    return {
      character() {
        return shopItems[customState] // Все товары character
      },
      room() {
        return shopItems[customState] // Все товары room
      },
      beauties() {
        return filteredSlots.find((item) => item.system_name === filter) ?? {} // Товары активного фильтра
      },
    }[customState]?.()
  }, [shopItems, customState, filter])

  const isPending = useBuyOrPutItem({ updateBeautiesBalance: gameTamagotchiBeauties })

  // На localhost/test не ждём одевания эльфа — isDressed не блокирует лоадер
  const noWaitForDressed = !isLocalOrTestHost() && !isDressed

  // Логика шагов при смене таба
  useEffect(() => {
    const stepsByState = {
      room() {
        eventRooms?.length > 1 && typeof exeptionRoom !== 'string'
          ? setCustomStep(stepsLogic[customState])
          : setCustomStep('filters')

        webCatalogProductsSku.current = []
      },
      character() {
        setCustomStep(stepsLogic[customState])

        webCatalogProductsSku.current = []
      },
      beauties() {
        setCustomStep(stepsLogic[customState])

        if (!webCatalogProductsSku.current?.length) {
          return
        }

        // Запрашиваем подробную информацию по продуктам
        getWebCatalogProductsBySku(webCatalogProductsSku.current)
      },
    }

    stepsByState[customState]?.()

    setFilter(null)
  }, [customState, eventRooms])

  useEffect(() => {
    setCustomStep(!filter ? 'filters' : 'list')
  }, [filter])

  useEffect(() => {
    gsap.globalTimeline.resume()

    if (gameTamagotchiBeauties) {
      callbacks.getBeautiesBalance()
    }

    // Запрашиваем актуальные данные пользователя
    callbacks.getUserProfile()
  }, [])

  useCustomizationInfoModals()

  const customizationToggleItems = customizationContent.toggle

  const bannerCondition = useMemo(
    () => gameTamagotchiBeauties && customState === 'beauties' && customStep === 'filters',
    [customState, customStep, gameTamagotchiBeauties],
  )

  // Эльфа скрываем в случаях:
  // 1. Таб комнаты.
  // 2. Таб бьютисов и фильтр комнаты.
  // 2. Таб бьютисов и фильтр ванной.
  const isHideElf = useMemo(() => {
    return (
      customState === 'room' ||
      (customState === 'beauties' && filter === 'room') ||
      (customState === 'beauties' && filter === 'shower')
    )
  }, [customState, filter])

  // Кастомный колбэк для скрытия ванной
  const customCallbacks = useMemo(
    () =>
      customState === 'beauties' && filter === 'shower'
        ? () => callbacks.setIsBath(false)
        : undefined,
    [customState, filter, callbacks],
  )

  // Меняем комнату на ванную в случае есть таб бьютисов и фильтр ванной
  useEffect(() => {
    if (customState === 'beauties' && filter === 'shower') {
      callbacks.setIsBath()
    }
  }, [customState, filter])

  // Активный индекс таба
  const startActiveToggle = useMemo(
    () => customizationToggleItems.findIndex(({ action }) => action === customState),
    [customizationToggleItems, customState],
  )

  useChangesAugust2026(august2026ChangesData.customization.serviceDataKey)

  return (
    <section
      className={classNames(`customization customization_${customState}`, {
        'tamagotchi-2026': true,
      })}
      ref={ref}
    >
      <div
        className={classNames(`customization__header ${isWeb ? '' : 'app'}`, {
          ['customization__header_lang']: defaultStore !== stores.ru,
        })}
      >
        <StatusPanel
          callbacks={callbacks}
          type={'customization'}
        />
        <span className={'customization__stub'} />
      </div>
      <div className={'customization__content'}>
        <div className={'customization__background'}>
          <Room
            callbacks={callbacks}
            isHideElf={isHideElf}
          />
        </div>
        <div className={'customization__box'}>
          <Scrollbar
            {...customizationScrollbarSettings}
            ref={scrollRef}
          >
            <CSSTransition
              in={['filters', 'rooms'].includes(customStep)}
              timeout={{ enter: 300, exit: 0 }}
              classNames={'toggle'}
              mountOnEnter={true}
              unmountOnExit={true}
              onExited={() => scrollRef.current?.scrollToTop()}
            >
              <Toggle
                list={customizationToggleItems}
                funcs={shopState}
                startActive={startActiveToggle}
              />
            </CSSTransition>

            <SwitchTransition>
              <CSSTransition
                key={`${customState}-${customStep}-${filter}`}
                timeout={{ enter: 300, exit: 0 }}
                classNames={'customization__main'}
              >
                <div className={'customization__main'}>
                  {bannerCondition && <CustomizationBanner />}

                  {
                    {
                      rooms: (
                        <CustomizationRooms
                          callbacks={callbacks}
                          setCustomStep={setCustomStep}
                          setFilter={setFilter}
                          rooms={eventRooms}
                        />
                      ),
                      filters: isVisibleCustomizationSlots && (
                        <CustomizationFilters
                          key={'filters'}
                          filters={filteredSlots}
                          setCustomStep={setCustomStep}
                          setFilter={setFilter}
                          customState={customState}
                          callbacks={callbacks}
                        />
                      ),
                      list: isVisibleCustomizationSlots && (
                        <CustomizationBlock
                          key={'list'}
                          list={filteredList}
                          filter={filter}
                          setFilter={setFilter}
                          callbacks={callbacks}
                          customState={customState}
                          customCallbacks={customCallbacks}
                          isBeauties={gameTamagotchiBeauties && customState === 'beauties'}
                        />
                      ),
                    }[customStep]
                  }
                </div>
              </CSSTransition>
            </SwitchTransition>
          </Scrollbar>
        </div>
      </div>
      {(isPending || noWaitForDressed) && <AppLoader />}
    </section>
  )
})

export default Customization
Customization.propTypes = {}
